import _ from "lodash";

import { HasManyRelation } from "./decorators";
import useStore from "./store";
import transformValue from "./transformers/transform-value";

class BaseModel {
  id;
  __type;
  static safe = false;

  static assignAttributes(obj, attrs) {
    const attributeTypes = obj.constructor.attributes || {};
    for (const [key, type] of Object.entries(attributeTypes)) {
      const snakeCaseKey = _.snakeCase(key);
      if (attrs !== null && (attrs[snakeCaseKey] !== undefined || attrs[key] !== undefined)) {
        const a = transformValue(attrs[snakeCaseKey] ?? attrs[key] ?? null, type);
        obj[key] = a;
      }
    }

    obj.id = attrs?.id ?? attrs?.pk;
  }

  static assignRelationships(obj, attrs, opts = {}) {
    const relationships = obj.constructor.relationships || [];

    // basic relationships
    for (const { modelName, propertyName } of relationships) {
      const attrKey = _.snakeCase(propertyName);
      const value = attrs && attrs[attrKey];
      if (value === undefined) {
        continue;
      }

      const hasMany = modelName instanceof HasManyRelation;
      const RelatedModelName = hasMany ? modelName.className : modelName;
      const RelatedModel = opts.modelRegistry[RelatedModelName];
      if (!RelatedModel) {
        continue;
      }

      if (hasMany && Array.isArray(value)) {
        obj[propertyName] = value.map((item) => {
          const instance = new RelatedModel();
          instance.__type = modelName.className;
          this.assignAttributes(instance, item);
          this.assignRelationships(instance, item, opts);
          return instance;
        });
      } else {
        const instance = new RelatedModel();
        instance.__type = modelName.className;
        this.assignAttributes(instance, value);
        this.assignRelationships(instance, value, opts);
        obj[propertyName] = instance;
      }
    }

    // dynamic relationships
    const dynamicRelationships = obj.constructor.dynamicRelationships || [];
    for (const { modelName, propertyName } of dynamicRelationships) {
      const prefix = modelName.prefix;
      const RelatedModelName = modelName.className;
      const RelatedModel = opts.modelRegistry[RelatedModelName];
      if (!RelatedModel) {
        continue;
      }

      const dynamicValues = Object.entries(attrs)
        .filter(([key]) => key.startsWith(prefix))
        .reduce((acc, [key, val]) => {
          if (!val) {
            return acc;
          }
          const instance = new RelatedModel();
          instance.__type = modelName.className;
          this.assignAttributes(instance, val, opts);
          this.assignRelationships(instance, val, opts);
          acc[key] = instance;
          return acc;
        }, {});

      obj[propertyName] = dynamicValues;
    }
  }

  static fromJson(json, type, opts = {}) {
    const a = new this();
    a.__type = type;
    this.assignAttributes(a, json, opts);
    this.assignRelationships(a, json, opts);
    return a;
  }

  serialize() {
    const attributeTypes = this.constructor.attributes || {};

    return JSON.stringify(
      Object.fromEntries(
        Object.entries(this)
          .filter(([key]) => Object.prototype.hasOwnProperty.call(attributeTypes, key))
          .map(([key, value]) => [_.snakeCase(key), serializeValue(value, attributeTypes[key])])
      )
    );
  }

  async save(settings) {
    return await useStore.getState().__save(this, settings);
  }
}

// Function to handle serialization before sending to API
function serializeValue(value, type) {
  if (value === null) {
    return value;
  }

  switch (type) {
    case "datetime":
      return value instanceof Date ? value.toISOString() : value;
    case "number":
      return Number(value);
    case "boolean":
      return Boolean(value);
    case "string":
    default:
      return String(value);
  }
}

export default BaseModel;
